import React from "react";
import { useNavigate } from "react-router-dom";
import { Droplets, Lock } from "lucide-react";
import { FollowWashProsBlock } from "@/components/shared/LegalFooter";

/**
 * HomeFooter — public-page footer for the customer-facing marketing
 * site (PremiumHomePage, SEO pages, waitlist confirmations, etc.).
 *
 * Phase 7g.15.98 (Rick QA clarification 2026-06-09 12:55 UTC):
 *   On phone vertical view (390×844 / 360×800) the footer rendered as
 *   one visually merged section because (a) there were no horizontal
 *   dividers between grid children, (b) low-contrast slate-400/500/600
 *   text faded into the light-mode gradient background, and (c) two
 *   of the five grid children crammed multiple sub-blocks together
 *   (Partner + Investors, Follow + Legal). Restructured so the mobile
 *   footer renders as FIVE distinct, clearly-bordered, section-headered
 *   stacked blocks:
 *
 *     1. Brand              (WashPros / Concierge / mission / WSH™)
 *     2. Company            (About, FAQ, Blog, Contact)
 *     3. Services           (9 category links + descriptions)
 *     4. Partner / Investors / Social  (vendor/franchise/fleet +
 *                                       investors email + 5 social links)
 *     5. Legal & Trust / Trademark     (policies + © line + IP notice
 *                                       + final category list)
 *
 *   Desktop (md+) retains the existing 5-column grid; mobile dividers
 *   are suppressed via `md:border-t-0 md:pt-0`. No backend changes.
 */
const HomeFooter = () => {
  const navigate = useNavigate();

  const footerLinks = {
    company: [
      { label: "About Us", path: "/about" },
      { label: "FAQ", path: "/faq" },
      { label: "Blog", path: "/blog" },
      { label: "Contact Us", path: "/contact" },
    ],
    services: [
      { label: "Car Wash",                desc: "Mobile pro hand wash & detail",                       path: "/services/car-wash" },
      { label: "Boat Detailing",          desc: "Salt-safe marine gelcoat care",                       path: "/services/boat" },
      { label: "RV Washing",              desc: "On-site motorhome & trailer",                         path: "/services/rv" },
      { label: "Golf Carts",              desc: "Community driveway & garage detail",                  path: "/waitlist?category=golf-cart" },
      { label: "Luxury & Exotic",         desc: "Garage-Keeper's insured specialists",                 path: "/waitlist?category=luxury" },
      { label: "Collectibles & Classics", desc: "Concours-grade show-prep",                            path: "/waitlist?category=classic" },
      { label: "Fleet Services",          desc: "Recurring scheduled B2B service",                     path: "/services/fleet" },
      { label: "Recreational Vehicles",   desc: "ATVs · UTVs · jet skis · toys",                       path: "/waitlist?category=recreational" },
      // Phase 7g.15.47 — Aircraft Detailing footer link (Rick QA 2026-05-25).
      // Cosmetic-only copy; never imply maintenance / inspection /
      // de-icing / airworthiness.
      { label: "Aircraft Detailing",      desc: "Small aircraft cosmetic detailing — custom quote only", path: "/aircraft-detailing" },
    ],
    partner: [
      { label: "Become a Vendor",         path: "/vendors/apply" },
      { label: "Vendor Supply Store",     path: "/vendor/store" },
      { label: "Franchise Opportunities", path: "/franchise" },
      { label: "Fleet Partnerships",      path: "/contact?tab=fleet" },
    ],
    legal: [
      { label: "Trust & Policy Center", path: "/legal" },
      { label: "Terms of Service",      path: "/terms" },
      { label: "Privacy Policy",        path: "/privacy" },
      // Phase 7g.15.48 (Rick 2026-05-31): expose /sms-consent in the
      // public footer so Twilio A2P 10DLC campaign reviewers can reach
      // the SMS opt-in proof page from any page on washpros.io.
      { label: "SMS Consent Policy",    path: "/sms-consent" },
      { label: "Copyright Policy",      path: "/copyright-policy" },
    ],
  };

  // Mobile-divider helper applied to grid children 2..5 (first child has
  // no top border on mobile because it sits flush with the parent
  // 2026-06-16 Phase 7g.15.108 (Rick batch) — mobile 5-section
  // consistency: every non-first section gets the same divider +
  // vertical padding, and the first section adds matching pb so the
  // rhythm is even across the stack. md:+ falls back to the grid gap.
  const sectionDividerCls =
    "pt-8 pb-2 mt-0 border-t border-white/10 md:border-t-0 md:pt-0 md:pb-0";
  const firstSectionMobileSpacerCls = "pb-8 md:pb-0";

  return (
    <footer className="border-t border-white/10 py-10 sm:py-12" data-testid="home-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-y-0 gap-x-8 md:gap-y-8 mb-8">

          {/* ────────────────────────────────────────────────────
              SECTION 1 / 5 — BRAND
             ──────────────────────────────────────────────────── */}
          <div data-testid="home-footer-section-brand" className={`md:col-span-1 ${firstSectionMobileSpacerCls}`}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                <Droplets className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <span className="text-white font-bold text-lg block leading-tight">WashPros</span>
                <p className="text-cyan-400 text-xs leading-tight">The Car Wash Concierge</p>
              </div>
            </div>
            <p className="text-slate-300 text-sm mb-3 break-words leading-relaxed">
              Premium mobile car washing and detailing services delivered to your location.
            </p>
            <p
              className="text-amber-400 text-sm font-semibold break-words"
              data-testid="home-footer-wax-shine-tagline"
            >
              Wax &amp; Shine Headquarters<sup className="text-[0.6em] ml-0.5">™</sup>
            </p>
          </div>

          {/* ────────────────────────────────────────────────────
              SECTION 2 / 5 — COMPANY
             ──────────────────────────────────────────────────── */}
          <div data-testid="home-footer-section-company" className={sectionDividerCls}>
            <h4 className="text-white font-semibold mb-3 text-base">Company</h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <button
                    onClick={() => navigate(link.path)}
                    className="text-slate-300 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* ────────────────────────────────────────────────────
              SECTION 3 / 5 — SERVICES
             ──────────────────────────────────────────────────── */}
          <div data-testid="home-footer-section-services" className={sectionDividerCls}>
            <h4 className="text-white font-semibold mb-3 text-base">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.path}>
                  <button
                    onClick={() => navigate(link.path)}
                    className="group text-left w-full"
                    data-testid={`footer-service-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <span className="block text-slate-200 group-hover:text-cyan-300 transition-colors text-sm font-medium break-words">
                      {link.label}
                    </span>
                    {link.desc && (
                      <span className="block text-slate-400 group-hover:text-slate-300 transition-colors text-[11px] mt-0.5 break-words">
                        {link.desc}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* ────────────────────────────────────────────────────
              SECTION 4 / 5 — PARTNER / INVESTORS / SOCIAL
             ──────────────────────────────────────────────────── */}
          <div data-testid="home-footer-section-partner" className={sectionDividerCls}>
            <h4 className="text-white font-semibold mb-3 text-base">Partner With Us</h4>
            <ul className="space-y-2 mb-5">
              {footerLinks.partner.map((link) => (
                <li key={link.path}>
                  <button
                    onClick={() => navigate(link.path)}
                    className="text-slate-300 hover:text-white transition-colors text-sm"
                    data-testid={`footer-partner-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>

            <h4 className="text-white font-semibold mb-2 text-base">Investors</h4>
            <p className="text-slate-400 text-xs mb-2 break-words" data-testid="investor-cta-line">
              For investor or strategic partnership inquiries, contact{" "}
              <a
                href="mailto:investors@washpros.io"
                className="text-amber-400 hover:text-amber-300 transition-colors font-medium break-all"
              >
                investors@washpros.io
              </a>.
            </p>
            <a
              href="mailto:investors@washpros.io"
              className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors text-sm font-medium mb-5 break-all"
              data-testid="investor-email-link"
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              investors@washpros.io
            </a>

            <div className="pt-3 border-t border-white/5 md:border-t-0 md:pt-0">
              <FollowWashProsBlock />
            </div>
          </div>

          {/* ────────────────────────────────────────────────────
              SECTION 5 / 5 — LEGAL & TRUST / TRADEMARK
             ──────────────────────────────────────────────────── */}
          <div data-testid="home-footer-section-legal" className={sectionDividerCls}>
            <h4 className="text-white font-semibold mb-3 text-base">Legal &amp; Trust</h4>
            <ul className="space-y-2 mb-6" data-testid="home-footer-legal-list">
              {footerLinks.legal.map((link) => (
                <li key={link.path}>
                  <button
                    onClick={() => navigate(link.path)}
                    className="text-slate-300 hover:text-white transition-colors text-sm"
                    data-testid={`footer-legal-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
              {/* P1.4 follow-up (2026-06-17, Rick directive) — discreet
                  Staff Login affordance. Intentionally subtle (smaller,
                  muted, lock icon, last in the legal list) so the
                  public site doesn't feel "back-officey" but Ray /
                  Steven / Dan and any approved investor demo viewer
                  can reach /admin/login without typing the URL. */}
              <li className="pt-2 mt-2 border-t border-white/5">
                <button
                  onClick={() => navigate("/admin/login")}
                  className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-300 transition-colors text-xs"
                  data-testid="footer-staff-login"
                  aria-label="Staff Login"
                  title="Staff Login"
                >
                  <Lock className="w-3 h-3" aria-hidden="true" />
                  Staff Login
                </button>
              </li>
            </ul>

            {/* Trademark / Copyright / Patent badges. Inlined into the
                Legal section on mobile so the user sees the full legal
                story as one contiguous block. On desktop these still
                also appear in the bottom-bar via the `hidden md:flex`
                pattern below. */}
            <div className="md:hidden space-y-3" data-testid="home-footer-mobile-legal-inline">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-slate-400 text-xs">
                <span className="px-2 py-0.5 bg-slate-800/50 rounded text-[10px] font-medium">®</span>
                <span className="break-words">Federally Registered Trademark</span>
                <span className="px-2 py-0.5 bg-slate-800/50 rounded text-[10px] font-medium">℠</span>
                <span className="break-words">FL Trademarked</span>
                <span className="px-2 py-0.5 bg-slate-800/50 rounded text-[10px] font-medium">©</span>
                <span className="break-words">Registered Copyright</span>
                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-[10px] font-medium break-words">Utility Patent Pending</span>
              </div>
              <p className="text-slate-400 text-xs break-words leading-relaxed">
                © 2026 <span className="font-semibold text-slate-300">WashPros Global Services LLC</span>. All Rights Reserved.
              </p>
              <p className="text-slate-500 text-[11px] break-words leading-relaxed" data-testid="footer-trademark-line-mobile">
                WashPros<sup className="text-[0.6em]">®</sup> | WashPros Fleet™ | The Car Wash Concierge Company™ | Wax &amp; Shine Headquarters™
              </p>
              <p
                className="text-slate-500 text-[11px] break-words leading-relaxed"
                data-testid="footer-ip-protection-notice-mobile"
              >
                WashPros™, WashPros Fleet™, The Car Wash Concierge Company™, and Wax and Shine Headquarters™ are trademarks of WashPros Global Services LLC. All platform content, brand assets, workflows, software, designs, and copyrighted materials are protected.
              </p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-400 text-xs pt-2" data-testid="footer-category-list-mobile">
                <span>Cars</span><span className="text-cyan-500">•</span>
                <span>Boats</span><span className="text-cyan-500">•</span>
                <span>RVs</span><span className="text-cyan-500">•</span>
                <span>Golf Carts</span><span className="text-cyan-500">•</span>
                <span>Luxury</span><span className="text-cyan-500">•</span>
                <span>Collectibles</span><span className="text-cyan-500">•</span>
                <span>Fleets</span><span className="text-cyan-500">•</span>
                <span>Rec Vehicles</span>
              </div>
            </div>
          </div>
        </div>

        {/* ────────────────────────────────────────────────────────
            DESKTOP-ONLY BOTTOM BAR
            Hidden on phone vertical because the same content is now
            inlined inside SECTION 5 above. Keeps the desktop visual
            identity unchanged so existing public-page tests / SEO
            screenshots don't regress.
           ──────────────────────────────────────────────────────── */}
        <div className="hidden md:block border-t border-white/10 pt-8 space-y-4">
          <div className="flex flex-wrap items-center justify-center gap-2 text-slate-400 text-xs">
            <span className="px-2 py-0.5 bg-slate-800/50 rounded text-[10px] font-medium">®</span>
            <span>Federally Registered Trademark</span>
            <span className="text-slate-600">•</span>
            <span className="px-2 py-0.5 bg-slate-800/50 rounded text-[10px] font-medium">℠</span>
            <span>FL Trademarked</span>
            <span className="text-slate-600">•</span>
            <span className="px-2 py-0.5 bg-slate-800/50 rounded text-[10px] font-medium">©</span>
            <span>Registered Copyright</span>
            <span className="text-slate-600">•</span>
            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-[10px] font-medium">Utility Patent Pending</span>
          </div>

          <div className="flex flex-row items-center justify-between gap-4">
            <div className="text-left">
              <p className="text-slate-500 text-sm">
                © 2026 <span className="font-semibold text-slate-400">WashPros Global Services LLC</span>. All Rights Reserved.
              </p>
              <p className="text-slate-600 text-xs mt-1" data-testid="footer-trademark-line">
                WashPros<sup className="text-[0.6em]">®</sup> | WashPros Fleet™ | The Car Wash Concierge Company™ | Wax &amp; Shine Headquarters™
              </p>
              <p
                className="text-slate-600 text-[11px] mt-1 max-w-md"
                data-testid="footer-ip-protection-notice"
              >
                WashPros™, WashPros Fleet™, The Car Wash Concierge Company™, and Wax and Shine Headquarters™ are trademarks of WashPros Global Services LLC. All platform content, brand assets, workflows, software, designs, and copyrighted materials are protected.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-500 text-xs md:text-sm">
              <span>Cars</span>
              <span className="text-cyan-500">•</span>
              <span>Boats</span>
              <span className="text-cyan-500">•</span>
              <span>RVs</span>
              <span className="text-cyan-500">•</span>
              <span>Golf Carts</span>
              <span className="text-cyan-500">•</span>
              <span>Luxury</span>
              <span className="text-cyan-500">•</span>
              <span>Collectibles</span>
              <span className="text-cyan-500">•</span>
              <span>Fleets</span>
              <span className="text-cyan-500">•</span>
              <span>Rec Vehicles</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default HomeFooter;
